import {
  CartPromotionGroup,
  ICustomerTierDescription,
  ITierDescription,
} from '@teko/cart/v2';
import { InforOutlined } from '@teko/ds-icon';
import {
  Body,
  BottomSheet,
  ButtonChevron,
  Caption,
  Col,
  Modal,
  ModalBody,
  Row,
  Shape,
  styled,
} from '@teko/ds-mobile';
import promotionBadge from 'assets/promotion-badge.svg';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { flattenPromotionsFromPromotionGroups } from 'modules/cart/utils';
import React, { useMemo, useState } from 'react';

const MAX_DISPLAY_DESCRIPTIONS = 3;

interface ITierPromotionsProps {
  productPromotions: CartPromotionGroup[];
  // to show text not applicable with combo or not
  isApplicableWithCombo?: boolean;
  maxLinesBeforeEllipsis?: number;
  // epicOmni1585 show promotions for customer tier
  isCustomerTierPromotions?: boolean;
}

export const TierPromotions: React.FC<ITierPromotionsProps> = (props) => {
  const {
    productPromotions,
    isApplicableWithCombo = true,
    maxLinesBeforeEllipsis = 1,
    isCustomerTierPromotions = false,
  } = props;
  const { isMobile, epicOmni1643 } = useTenantConfig();
  const [showDetail, setShowDetail] = useState(false);

  // all promotions of an item (not being grouped)
  const allItemPromotions =
    flattenPromotionsFromPromotionGroups(productPromotions);

  // all tier descriptions of an item (not being grouped)
  const allItemTierDescriptions = useMemo(() => {
    const flattedTierDescriptions = !!allItemPromotions.length
      ? allItemPromotions.reduce<ITierDescription[]>(
          (previousTierDescriptions, currentPromotion) => [
            ...previousTierDescriptions,
            ...(currentPromotion.tierDescriptions || []),
          ],
          [],
        )
      : [];
    return flattedTierDescriptions.length > 0
      ? // filter out `tierDescriptions` with empty `descriptions` array
        flattedTierDescriptions.filter(
          (tierDesc) => !!tierDesc.descriptions.length,
        )
      : [];
  }, [allItemPromotions]);

  const allItemHightLightDescriptions = useMemo(() => {
    const flattedHightLightDescriptions = !!allItemPromotions.length
      ? allItemPromotions.reduce<string[]>(
          (previousHightLightDescriptions, currentPromotion) =>
            !!currentPromotion.highlightedDescriptions?.length
              ? [
                  ...previousHightLightDescriptions,
                  ...currentPromotion.highlightedDescriptions,
                ]
              : previousHightLightDescriptions,
          [],
        )
      : [];
    return flattedHightLightDescriptions;
  }, [allItemPromotions]);

  const allItemCustomerTierDescription = useMemo(() => {
    const flattedCustomerTierDescriptions = !!allItemPromotions.length
      ? allItemPromotions.reduce<ICustomerTierDescription[]>(
          (previousTierDescriptions, currentPromotion) => [
            ...previousTierDescriptions,
            ...(currentPromotion.customerTierDescriptions || []),
          ],
          [],
        )
      : [];
    return flattedCustomerTierDescriptions;
  }, [allItemPromotions]);

  // displaying tier descriptions limit to MAX_DISPLAY_DESCRIPTIONS
  const displayTierDescriptions = useMemo(() => {
    return allItemTierDescriptions.length > MAX_DISPLAY_DESCRIPTIONS
      ? allItemTierDescriptions.slice(0, MAX_DISPLAY_DESCRIPTIONS)
      : allItemTierDescriptions;
  }, [allItemTierDescriptions]);

  const handleCloseDetailModal = () => setShowDetail(false);

  const renderDetailContent = () =>
    allItemTierDescriptions.map((tierDesc, index) =>
      tierDesc.descriptions?.length ? (
        <TierDescList
          key={index}
          marginBottom={
            index !== allItemTierDescriptions.length - 1 ? '8px' : '0'
          }
        >
          <b>{tierDesc.title}</b>:<br />
          {tierDesc.descriptions.map((desc, subIndex) => (
            <li key={`${index}-${subIndex}`}>{desc}</li>
          ))}
        </TierDescList>
      ) : null,
    );

  if (
    (!allItemTierDescriptions.length && !isCustomerTierPromotions) ||
    (isCustomerTierPromotions && !allItemCustomerTierDescription.length)
  )
    return null;

  return (
    <>
      {!!displayTierDescriptions.length ||
      (!!allItemCustomerTierDescription.length && isCustomerTierPromotions) ? (
        <Shape padding={['s8', 's0']} backgroundColor="white">
          <PromotionBox
            borderRadius="m"
            padding="s8"
            border={epicOmni1643 ? undefined : '1px solid #00973c33'}
            epicOmni1643={epicOmni1643}
          >
            <StyledLabel epicOmni1643={epicOmni1643}>
              {!epicOmni1643 && (
                <img src={promotionBadge} alt="Promotion Badge" />
              )}
              <Caption
                level={1}
                weight="medium"
                color={epicOmni1643 ? 'white' : undefined}
              >
                {isCustomerTierPromotions
                  ? t('Ưu đãi khách hàng thành viên')
                  : t('Mua nhiều thêm Ưu đãi')}
              </Caption>
            </StyledLabel>

            <Shape marginTop="s28">
              <StyledUl>
                {isCustomerTierPromotions
                  ? allItemCustomerTierDescription.map((tierDesc, index) =>
                      !!tierDesc.description ? (
                        <li>
                          <Body
                            weight="regular"
                            line={maxLinesBeforeEllipsis}
                            color="#000"
                            key={index}
                            marginBottom="s4"
                          >
                            {tierDesc.title} {tierDesc.description}
                          </Body>
                        </li>
                      ) : null,
                    )
                  : displayTierDescriptions.map((tierDesc, index) =>
                      tierDesc.descriptions?.length > 0 ? (
                        <li>
                          <Body
                            weight="regular"
                            line={maxLinesBeforeEllipsis}
                            color="#000"
                            key={index}
                            marginBottom="s4"
                          >
                            {tierDesc.title}{' '}
                            {tierDesc.descriptions?.join(' & ')}
                          </Body>
                        </li>
                      ) : null,
                    )}
              </StyledUl>
            </Shape>

            <Row
              justify={
                !isApplicableWithCombo || !!allItemHightLightDescriptions.length
                  ? 'space-between'
                  : 'end'
              }
              align="bottom"
            >
              <Col>
                {[
                  !isApplicableWithCombo && t('Không áp dụng cùng combo'),
                  !!allItemHightLightDescriptions.length &&
                    allItemHightLightDescriptions[0],
                ]
                  .filter(Boolean)
                  .map((message, index) => {
                    const color = epicOmni1643 ? 'warning' : '#00973C';
                    return (
                      <Row key={index} align="middle" justify="start">
                        {!epicOmni1643 && (
                          <InforOutlined size={16} color={color} />
                        )}
                        <Body color={color} marginLeft="s4">
                          {message}
                        </Body>
                      </Row>
                    );
                  })}
              </Col>
              {!isCustomerTierPromotions && !epicOmni1643 && (
                <Col>
                  <StyledDetailButton onClick={() => setShowDetail(true)}>
                    {t('Xem chi tiết')}
                  </StyledDetailButton>
                </Col>
              )}
            </Row>
            {epicOmni1643 && !isCustomerTierPromotions && (
              <Row justify="center">
                <ButtonChevron
                  direction="down"
                  style={{ border: 'none' }}
                  onClick={() => setShowDetail(true)}
                >
                  {t('Xem thêm')}
                </ButtonChevron>
              </Row>
            )}
          </PromotionBox>
        </Shape>
      ) : null}

      {!!showDetail &&
        (!!isMobile ? (
          <StyledBottomSheet
            show={showDetail}
            title={t('Chi tiết khuyến mãi')}
            closeBottomSheet={handleCloseDetailModal}
            backdropClicked={handleCloseDetailModal}
            contentHeight="500px"
            zIndex={1060}
          >
            <Shape padding={['s4', 's16']}>{renderDetailContent()}</Shape>
          </StyledBottomSheet>
        ) : (
          <StyledModal
            width={600}
            visible={showDetail}
            title={t('Chi tiết khuyến mãi')}
            centeredTitle
            okText=""
            cancelText={t('Đóng')}
            onCancel={handleCloseDetailModal}
            onClose={handleCloseDetailModal}
            showCloseIcon={false}
          >
            <StyledModalBody>{renderDetailContent()}</StyledModalBody>
          </StyledModal>
        ))}
    </>
  );
};

/* ---------------- Styled ---------------- */
const PromotionBox = styled(Shape)<{ epicOmni1643?: boolean }>(
  ({ epicOmni1643, theme }) => `
  position: relative;
  background: 
    ${
      !epicOmni1643
        ? 'linear-gradient(180deg,rgba(0, 151, 60, 0.1) 0%,rgba(0, 151, 60, 0) 100%)'
        : theme.colors.secondary50
    };
`,
);

const StyledLabel = styled.div<{ epicOmni1643?: boolean }>(
  ({ epicOmni1643, theme }) => `
  position: absolute;
  top: 0;
  left: 0;
  width: max-content;
  height: 28px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-top-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  padding: 0.25rem 1rem 0.25rem 1rem;
  background: ${
    !epicOmni1643
      ? 'linear-gradient(90deg, #fae14c 59.21%, #00973c 100%)'
      : theme.colors.primary
  };
`,
);

const StyledDetailButton = styled(Body)<{ epicOmni1643?: boolean }>`
  cursor: pointer;
  color: #00973c;
  font-weight: 500;

  &:hover {
    color: #006b2a;
  }
`;

const StyledModal = styled(Modal)`
  padding: 24px;
`;

const StyledModalBody = styled(ModalBody)`
  padding: 0;
  max-height: 600px;
  overflow-y: auto;
`;

const TierDescList = styled.ul(
  ({ marginBottom }: { marginBottom: string | number }) => `
  list-style-position: inside;
  margin-bottom: ${marginBottom};
`,
);

const StyledBottomSheet = styled(BottomSheet)`
  width: 100%;
`;

const StyledUl = styled.ul`
  margin: 0;
  padding-left: 0; // remove default padding of ul
  margin-left: 2rem;
`;
